/**
 * nicknameTemplateStore — the trainer's saved IV-naming template, on-device.
 *
 * One template per device (localStorage, same pattern as goPassRankStore):
 * either a preset id from NICKNAME_PRESETS or a custom token list built in
 * the Naming page. Local-only: a naming style is personalisation, not dex
 * data, so it does not sync.
 */
import { useSyncExternalStore } from "react";
import {
  DEFAULT_NICKNAME_PRESET_ID,
  NICKNAME_DIGIT_STYLES,
  RETIRED_NICKNAME_PRESET_IDS,
  presetById,
  type NicknameTemplate,
  type NicknameToken,
  type NicknameTokenKind,
} from "@/lib/ivNicknames";

const STORAGE_KEY = "nexus.nicknameTemplate.v1";
/** Fired on every template save. Exported (2026-08-30) so an ACTIVE naming
 *  session can re-render its queue when the trainer switches presets. */
export const NICKNAME_TEMPLATE_CHANGE_EVENT = "nexus:nickname-template-changed";
const CHANGE_EVENT = NICKNAME_TEMPLATE_CHANGE_EVENT;

/** One trainer-saved preset: a named token layout of their own. */
export interface UserNicknamePreset {
  /** "u_" prefix keeps user ids disjoint from the built-in preset ids. */
  id: string;
  label: string;
  tokens: NicknameToken[];
}

/** Most user presets kept; the oldest drops when a save would exceed it. */
export const USER_PRESET_CAP = 12;
export const USER_PRESET_LABEL_MAX = 18;

export interface NicknameTemplateState {
  /**
   * Selected preset id: a built-in id, a user preset id ("u_..."), or
   * "custom" when the trainer edited the tokens away from any preset.
   */
  presetId: string;
  /** The live token list (kept in sync with the preset when one is picked). */
  tokens: NicknameToken[];
  /** The trainer's own saved presets (2026-08-29 ask), oldest first. */
  userPresets: UserNicknamePreset[];
  /**
   * Conditional triggers (2026-09-03, Pro): in order, the first enabled
   * trigger a Pokémon satisfies picks ITS preset instead of the default
   * layout above. Stored regardless of tier; honoured only for Pro.
   */
  triggers: NicknameTrigger[];
}

const TOKEN_KINDS: readonly NicknameTokenKind[] = [
  "species", "dexNumber", "cp", "hp", "level", "lucky", "shadow", "purified",
  "ivTriple", "ivPercent", "stars", "gender", "eliteMove",
  "targetSpecies", "targetCp", "targetHp", "bestLeague", "targetPct", "bestRank",
  "dustCost", "candyCost", "xlCost",
  "littleMark", "littlePct", "littleRank",
  "greatMark", "greatPct", "greatRank",
  "ultraMark", "ultraPct", "ultraRank",
  "masterMark", "masterPct", "masterRank",
  "bestPct", "text",
];

/* ── Conditional triggers (2026-09-03 owner ask, Pro) ─────────────────────── */

export type NicknameTriggerKind =
  | "hundo"
  | "pvp"
  | "favourite"
  | "cp"
  | "fullyEvolved"
  | "twoCharged"
  | "highIv";

export interface NicknameTrigger {
  id: string;
  kind: NicknameTriggerKind;
  /** pvp: best league percent, own or destiny; cp: CP; highIv: IV percent. Unused otherwise. */
  threshold?: number;
  /** A built-in preset id or a user preset id ("u_..."). */
  presetId: string;
  enabled: boolean;
}

export const NICKNAME_TRIGGER_KINDS: readonly NicknameTriggerKind[] = [
  "hundo",
  "pvp",
  "favourite",
  "cp",
  "fullyEvolved",
  "twoCharged",
  "highIv",
];

export const TRIGGER_CAP = 12;

function sanitizeTrigger(v: unknown): NicknameTrigger | null {
  if (!v || typeof v !== "object") return null;
  const t = v as Partial<NicknameTrigger>;
  if (typeof t.id !== "string" || !t.kind || !NICKNAME_TRIGGER_KINDS.includes(t.kind)) return null;
  if (typeof t.presetId !== "string" || t.presetId.length === 0) return null;
  const out: NicknameTrigger = { id: t.id, kind: t.kind, presetId: t.presetId, enabled: t.enabled !== false };
  if (typeof t.threshold === "number" && Number.isFinite(t.threshold)) out.threshold = t.threshold;
  return out;
}

function sanitizeToken(v: unknown): NicknameToken | null {
  if (!v || typeof v !== "object") return null;
  const t = v as Partial<NicknameToken>;
  if (!t.kind || !TOKEN_KINDS.includes(t.kind)) return null;
  const out: NicknameToken = { kind: t.kind };
  if (t.style && NICKNAME_DIGIT_STYLES.includes(t.style)) out.style = t.style;
  if (typeof t.text === "string" && t.text.length <= 4) out.text = t.text;
  if (typeof t.sep === "string" && t.sep.length <= 2) out.sep = t.sep;
  if (t.maxDark === true) out.maxDark = true;
  if (t.maxLight === true) out.maxLight = true;
  if (t.hash === true) out.hash = true;
  if (typeof t.maxLen === "number" && Number.isInteger(t.maxLen) && t.maxLen >= 2 && t.maxLen <= 10)
    out.maxLen = t.maxLen;
  return out;
}

function sanitizeTokens(v: unknown): NicknameToken[] {
  return Array.isArray(v)
    ? v.map(sanitizeToken).filter((t): t is NicknameToken => t != null)
    : [];
}

function sanitizeUserPreset(v: unknown): UserNicknamePreset | null {
  if (!v || typeof v !== "object") return null;
  const p = v as Partial<UserNicknamePreset>;
  if (typeof p.id !== "string" || !p.id.startsWith("u_")) return null;
  if (typeof p.label !== "string" || p.label.trim().length === 0) return null;
  const tokens = sanitizeTokens(p.tokens);
  if (tokens.length === 0) return null;
  return { id: p.id, label: p.label.slice(0, USER_PRESET_LABEL_MAX), tokens };
}

function defaultState(): NicknameTemplateState {
  const preset = presetById(DEFAULT_NICKNAME_PRESET_ID)!;
  return {
    presetId: preset.id,
    tokens: preset.template.tokens.map((t) => ({ ...t })),
    userPresets: [],
    triggers: [],
  };
}

function read(): NicknameTemplateState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultState();
    const parsed = JSON.parse(raw) as Partial<NicknameTemplateState>;
    const tokens = sanitizeTokens(parsed?.tokens);
    const userPresets = Array.isArray(parsed?.userPresets)
      ? parsed.userPresets
          .map(sanitizeUserPreset)
          .filter((p): p is UserNicknamePreset => p != null)
          .slice(0, USER_PRESET_CAP)
      : [];
    const triggers = Array.isArray(parsed?.triggers)
      ? parsed.triggers
          .map(sanitizeTrigger)
          .filter((t): t is NicknameTrigger => t != null)
          .slice(0, TRIGGER_CAP)
      : [];
    if (tokens.length === 0) return { ...defaultState(), userPresets, triggers };
    return {
      presetId: typeof parsed.presetId === "string" ? parsed.presetId : "custom",
      tokens,
      userPresets,
      triggers,
    };
  } catch {
    return defaultState();
  }
}

let snapshot: NicknameTemplateState =
  typeof localStorage === "undefined" ? defaultState() : read();

export function getNicknameTemplateState(): NicknameTemplateState {
  return snapshot;
}

/** The template the renderer consumes, from the saved state. */
export function getNicknameTemplate(): NicknameTemplate {
  return { tokens: snapshot.tokens };
}

function persist(next: Omit<NicknameTemplateState, "triggers"> & { triggers?: NicknameTrigger[] }): void {
  snapshot = { ...next, triggers: next.triggers ?? snapshot.triggers };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot));
  } catch {
    // Storage full/blocked: the in-memory snapshot still serves this session.
  }
  window.dispatchEvent(new Event(CHANGE_EVENT));
}

export function setNicknameTemplateState(
  state: Pick<NicknameTemplateState, "presetId" | "tokens">,
): void {
  persist({
    presetId: state.presetId,
    tokens: state.tokens.map((t) => ({ ...t })),
    userPresets: snapshot.userPresets,
  });
}

/** Replace the trigger list (the Naming page's Conditional Triggers editor). */
export function setNicknameTriggers(triggers: NicknameTrigger[]): void {
  persist({
    presetId: snapshot.presetId,
    tokens: snapshot.tokens.map((t) => ({ ...t })),
    userPresets: snapshot.userPresets,
    triggers: triggers
      .map(sanitizeTrigger)
      .filter((t): t is NicknameTrigger => t != null)
      .slice(0, TRIGGER_CAP),
  });
}

/** Tokens for a preset id: built-in, user, or null when it no longer exists. */
export function presetTokensById(
  id: string,
  userPresets: readonly UserNicknamePreset[] = snapshot.userPresets,
): NicknameToken[] | null {
  const builtIn = presetById(id);
  if (builtIn) return builtIn.template.tokens;
  const user = userPresets.find((p) => p.id === id);
  return user ? user.tokens : null;
}

/**
 * Save the CURRENT token layout as a new user preset and select it. Returns
 * the new preset id. At the cap the oldest user preset drops first.
 */
export function saveUserNicknamePreset(label: string): string {
  const id = `u_${Date.now().toString(36)}${Math.floor(Math.random() * 1296)
    .toString(36)
    .padStart(2, "0")}`;
  const clean = label.trim().slice(0, USER_PRESET_LABEL_MAX) || "My preset";
  const preset: UserNicknamePreset = {
    id,
    label: clean,
    tokens: snapshot.tokens.map((t) => ({ ...t })),
  };
  const kept = [...snapshot.userPresets, preset].slice(-USER_PRESET_CAP);
  persist({ presetId: id, tokens: snapshot.tokens.map((t) => ({ ...t })), userPresets: kept });
  return id;
}

/** Overwrite an existing user preset with the CURRENT token layout. */
export function updateUserNicknamePreset(id: string): void {
  if (!snapshot.userPresets.some((p) => p.id === id)) return;
  persist({
    presetId: id,
    tokens: snapshot.tokens.map((t) => ({ ...t })),
    userPresets: snapshot.userPresets.map((p) =>
      p.id === id ? { ...p, tokens: snapshot.tokens.map((t) => ({ ...t })) } : p,
    ),
  });
}

/** Delete a user preset. A deleted selected preset leaves tokens as custom. */
export function deleteUserNicknamePreset(id: string): void {
  persist({
    presetId: snapshot.presetId === id ? "custom" : snapshot.presetId,
    tokens: snapshot.tokens.map((t) => ({ ...t })),
    userPresets: snapshot.userPresets.filter((p) => p.id !== id),
    // A trigger aimed at a deleted preset would fire into nothing.
    triggers: snapshot.triggers.filter((t) => t.presetId !== id),
  });
}

function subscribe(onChange: () => void): () => void {
  const handler = () => {
    snapshot = read();
    onChange();
  };
  window.addEventListener(CHANGE_EVENT, handler);
  window.addEventListener("storage", handler);
  return () => {
    window.removeEventListener(CHANGE_EVENT, handler);
    window.removeEventListener("storage", handler);
  };
}

function getSnapshot(): NicknameTemplateState {
  return snapshot;
}

/** Reactive template state; re-renders on save (this tab or another). */
export function useNicknameTemplate(): NicknameTemplateState {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/**
 * One-shot refresh of the BUILT-IN preset templates on a device that is
 * already carrying one (2026-08-31, owner direction).
 *
 * Normally a stored template is the trainer's own business and we leave it
 * alone. This is the exception, and it earns it: the presets a trainer could
 * choose from before today were both narrower and partly BROKEN. The league
 * marker ignored its digit style outright, so "circled" on it did nothing; the
 * maxed-stat highlight had no light variant; and every preset reached for
 * superscript, so a trainer who wanted circled glyphs had no preset that
 * offered them. Their pick was made from a menu that could not show what the
 * app can actually do, so it is not much of a choice to preserve.
 *
 * PRECISE ABOUT WHOSE TEMPLATE IT TOUCHES. Every edit path in the Naming page
 * saves with presetId "custom" (only pickPreset passes a real id), so a stored
 * BUILT-IN id means "picked this preset and never touched it" — exactly the
 * trainers whose look was chosen from the old menu. So:
 *
 *   - a live built-in id  → tokens are replaced with that preset's tokens now
 *   - a RETIRED id        → kept verbatim as "custom": the layout is still
 *                           theirs and still renders, it just no longer names
 *                           a preset (the picker highlighted nothing for it)
 *   - "custom" or "u_…"   → untouched. A hand-built layout or a saved user
 *                           preset is a real choice, not a default.
 *
 * User presets are never touched in any branch.
 *
 * Returns true when it changed something (for the migration log).
 */
export function refreshBuiltInNicknamePresets(): boolean {
  const current = snapshot.presetId;
  if (current === "custom" || current.startsWith("u_")) return false;

  const preset = presetById(current);
  if (preset) {
    persist({
      presetId: current,
      tokens: preset.template.tokens.map((t) => ({ ...t })),
      userPresets: snapshot.userPresets,
    });
    return true;
  }
  if (RETIRED_NICKNAME_PRESET_IDS.includes(current)) {
    persist({
      presetId: "custom",
      tokens: snapshot.tokens,
      userPresets: snapshot.userPresets,
    });
    return true;
  }
  // An id we have never heard of: leave it. `read()` already falls back to the
  // default template for anything malformed, so there is nothing to repair.
  return false;
}

/** Test seam: force a fresh parse of localStorage (the sanitize path). */
export function __reloadNicknameTemplateForTest(): void {
  snapshot = read();
}

/** Test seam. */
export function __resetNicknameTemplateForTest(): void {
  snapshot = defaultState();
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}
